import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { 
  Sun, 
  CheckCircle, 
  Send
} from 'lucide-react';
import SEO from '../components/SEO';

const Landing: React.FC = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: ''
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [showPopup, setShowPopup] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Form submission logic would go here
    setIsSubmitted(true);
    setTimeout(() => setIsSubmitted(false), 5000);
  };

  const handlePopupSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Popup form submission logic would go here
    setIsSubmitted(true);
    setShowPopup(false);
    setTimeout(() => setIsSubmitted(false), 5000);
  };

  const benefits = [
    "Up to ₹78,000 government subsidy",
    "Free site survey & consultation",
    "25+ year warranty on panels",
    "Zero maintenance cost",
    "Reduce electricity bills by 90%",
    "Professional installation team"
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-900 via-blue-800 to-green-800 w-full overflow-x-hidden">
      <SEO
        title="Get ₹78,000 Solar Subsidy | Moulya solar energies"
        description="Limited-time PM Surya Ghar subsidy. Get a free consultation and quick quote for rooftop solar installation."
        path="/landing"
        image="/images/logo.webp"
        keywords="[PM Surya Ghar subsidy, solar subsidy Telangana, solar subsidy AP, rooftop solar offer, free solar quote, government solar scheme]"
      />
      {/* Header */}
      <header className="bg-white/10 backdrop-blur-sm border-b border-white/20 sticky top-0 z-50 w-full">
        <div className="w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6 py-2 sm:py-3">
          <div className="flex items-center justify-between w-full">
            {/* Company Logo */}
            <div className="flex items-center flex-shrink-0 min-w-0">
              <img 
                src="/images/logo.webp" 
                alt="Moulya solar energies" 
                className="h-8 sm:h-10 lg:h-12 w-auto object-contain"
              />
            </div>
            
            {/* Contact Buttons */}
            <div className="flex items-center space-x-1 sm:space-x-2 lg:space-x-3 flex-shrink-0">
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative py-12 sm:py-16 lg:py-20 text-center text-white">
        {/* Background Image for Hero Section */}
        <div className="absolute inset-0 bg-cover bg-center bg-no-repeat" style={{
          backgroundImage: `url('https://img.freepik.com/premium-photo/portrait-young-indian-technician-manager-wearing-formal-cloths-standing-with-solar-panel-renewable-energy-man-standing-crossed-arm-copy-space_255667-66750.jpg?semt=ais_hybrid&w=740&q=80')`
        }}></div>
        
        {/* Dark Overlay for Better Text Visibility */}
        <div className="absolute inset-0 bg-black/50"></div>
        
        {/* Hero Content */}
        <div className="relative z-10 w-full max-w-7xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 lg:gap-12 items-center">
            {/* Left Side - Main Content */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="text-left order-last lg:order-first"
            >
              <div className="bg-red-600 text-white px-3 sm:px-6 py-2 rounded-full inline-block mb-4 sm:mb-6 text-xs sm:text-sm font-semibold">
                🔥 LIMITED TIME OFFER
              </div>
              
              <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl xl:text-6xl font-bold mb-4 sm:mb-6 leading-tight">
                Install Solar Panels with
                <br className="sm:hidden" />
                <span className="hidden sm:inline"> </span><span className="text-yellow-400">Government Subsidy</span>
              </h1>
              
              <p className="text-base sm:text-lg md:text-xl lg:text-2xl mb-6 sm:mb-8 text-gray-200">
                Save money and the planet —
                <br className="hidden md:block" />
                reduce your electricity bill by up to <strong>90%</strong>
              </p>

              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-3 sm:p-6 lg:p-8 border border-white/20">
                <h2 className="text-lg sm:text-xl lg:text-2xl font-bold mb-3 sm:mb-4 text-center">Quick Solar Calculator</h2>
                <div className="grid grid-cols-3 gap-2 sm:gap-4 md:gap-6 text-center">
                  <div className="flex flex-col items-center">
                    <div className="text-lg sm:text-2xl lg:text-3xl font-bold text-yellow-400 mb-1 sm:mb-2">₹78,000</div>
                    <div className="text-gray-300 text-xs sm:text-sm lg:text-base">Maximum Subsidy</div>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="text-lg sm:text-2xl lg:text-3xl font-bold text-green-400 mb-1 sm:mb-2">90%</div>
                    <div className="text-gray-300 text-xs sm:text-sm lg:text-base">Bill Reduction</div>
                  </div>
                  <div className="flex flex-col items-center">
                    <div className="text-lg sm:text-2xl lg:text-3xl font-bold text-blue-400 mb-1 sm:mb-2">25+</div>
                    <div className="text-gray-300 text-xs sm:text-sm lg:text-base">Years Warranty</div>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right Side - Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="lg:pl-8 order-first lg:order-last"
            >
              <div className="bg-white/10 backdrop-blur-sm rounded-3xl p-4 sm:p-6 lg:p-8 border border-white/20 shadow-2xl">
                <div className="text-center mb-4 sm:mb-6">
                  <div className="w-12 h-12 sm:w-16 sm:h-16 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-3 sm:mb-4">
                    <Sun className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                  </div>
                  <h2 className="text-xl sm:text-2xl font-bold text-white mb-2">
                    Get ₹78,000 Subsidy
                  </h2>
                  <p className="text-gray-200 text-xs sm:text-sm">
                    Free consultation within 30 minutes
                  </p>
                </div>

                {isSubmitted && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-green-500/20 border border-green-400/50 text-green-200 px-4 py-3 rounded-xl mb-4 text-center text-sm"
                  >
                    <CheckCircle className="h-4 w-4 inline mr-2" />
                    Thank you! We'll call you within 30 minutes.
                  </motion.div>
                )}

                <form onSubmit={handleSubmit} className="space-y-3 sm:space-y-4">
                  <div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 sm:py-2.5 border border-white/20 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm bg-white/10 backdrop-blur-sm text-white placeholder-gray-300"
                      placeholder="Full Name *"
                    />
                  </div>

                  <div>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 sm:py-2.5 border border-white/20 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm bg-white/10 backdrop-blur-sm text-white placeholder-gray-300"
                      placeholder="Phone Number *"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-gray-900 font-bold py-2.5 sm:py-3 px-4 rounded-xl transition-all duration-200 transform hover:scale-105 text-sm sm:text-base shadow-lg"
                    style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}
                  >
                    <span className="inline-block">Get Free Consultation</span>
                  </button>
                </form>

                <div className="mt-3 sm:mt-4 text-center">
                  <p className="text-gray-300 text-xs">
                    Or call us directly: 
                  </p>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white/5 w-full">
        <div className="w-full max-w-6xl mx-auto px-3 sm:px-4 lg:px-6">
          <h2 className="text-3xl lg:text-4xl font-bold text-white text-center mb-12">
            Why Choose Moulya solar energies?
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-white/10 backdrop-blur-sm p-6 rounded-xl border border-white/20 text-center"
              >
                <CheckCircle className="h-8 w-8 text-green-400 mb-4 mx-auto" />
                <p className="text-white text-lg">{benefit}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Lead Form Section */}
      <section className="py-20 w-full">
        <div className="w-full max-w-4xl mx-auto px-3 sm:px-4 lg:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Form */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
              className="bg-gradient-to-br from-white to-gray-50 rounded-3xl shadow-2xl p-6 border border-gray-100"
            >
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Sun className="h-8 w-8 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-2">
                  Get ₹78,000 Subsidy
                </h2>
                <p className="text-gray-600 text-sm">
                  Free consultation within 30 minutes
                </p>
              </div>

              {isSubmitted && (
                <motion.div
                  initial={{ opacity: 0, y: -20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200 text-green-700 px-4 py-3 rounded-xl mb-4 text-center text-sm"
                >
                  <CheckCircle className="h-4 w-4 inline mr-2" />
                  Thank you! We'll call you within 30 minutes.
                </motion.div>
              )}

              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm bg-white/80 backdrop-blur-sm"
                      placeholder="Full Name *"
                    />
                  </div>
                  <div>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2.5 border border-gray-200 rounded-xl focus:ring-2 focus:ring-yellow-400 focus:border-transparent text-sm bg-white/80 backdrop-blur-sm"
                      placeholder="Phone Number *"
                    />
                  </div>
                </div>

                <button
                  type="submit"
                  className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-gray-900 py-3 px-6 rounded-xl font-bold text-lg transition-all transform hover:scale-105 shadow-lg hover:shadow-xl"
                  style={{ fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}
                >
                  <Send className="inline h-5 w-5 mr-2 text-gray-900" />
                  <span className="inline-block">Get Free Quote</span>
                </button>

                <p className="text-xs text-gray-500 text-center">
                  🔒 Your information is secure and private
                </p>
              </form>
            </motion.div>

            {/* Right Side Content */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-white"
            >
              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20 mb-8">
                <h3 className="text-2xl font-bold mb-4">Government Subsidy Details</h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-400 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <div className="font-semibold">Subsidy on System Cost –</div>
                      <div className="text-sm text-gray-300">Financial support directly reduces your upfront investment.</div>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-400 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <div className="font-semibold">Maximum Subsidy Amount –</div>
                      <div className="text-sm text-gray-300">Up to ₹78,000 available.</div>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-400 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <div className="font-semibold">Eligibility –</div>
                      <div className="text-sm text-gray-300">Applies to 1 kW to 3 kW rooftop solar systems.</div>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-green-400 mr-3 mt-1 flex-shrink-0" />
                    <div>
                      <div className="font-semibold">Disbursement –</div>
                      <div className="text-sm text-gray-300">Subsidy credited via Direct Bank Transfer (DBT) to the beneficiary.</div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border border-white/20">
                <h3 className="text-2xl font-bold mb-4">What Happens Next?</h3>
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3 mt-1">1</div>
                    <div>
                      <h4 className="font-semibold">Form Submission</h4>
                      <p className="text-gray-300 text-sm">Submit your details above</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3 mt-1">2</div>
                    <div>
                      <h4 className="font-semibold">Expert Call</h4>
                      <p className="text-gray-300 text-sm">Our expert calls within 30 minutes</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3 mt-1">3</div>
                    <div>
                      <h4 className="font-semibold">Free Survey</h4>
                      <p className="text-gray-300 text-sm">Free site survey & quote</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <div className="bg-blue-500 text-white rounded-full w-8 h-8 flex items-center justify-center text-sm font-bold mr-3 mt-1">4</div>
                    <div>
                      <h4 className="font-semibold">Installation</h4>
                      <p className="text-gray-300 text-sm">Professional installation with subsidy</p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 text-center w-full">
        <div className="w-full max-w-4xl mx-auto px-3 sm:px-4 lg:px-6">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-4xl lg:text-5xl font-bold text-white mb-6">
              Don't Miss Out on ₹78,000 Subsidy!
            </h2>
            <p className="text-xl text-gray-200 mb-8">
              Limited time offer. Get your free consultation today and start saving on electricity bills.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <p className="text-gray-200">
                Contact us for more information
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-black/30 py-8 text-center text-gray-300 w-full">
        <div className="w-full max-w-4xl mx-auto px-3 sm:px-4 lg:px-6">
          <p>&copy; 2025 Moulya solar energies. All rights reserved.</p>
          <p className="mt-2">| ISO 9001:2015 Certified Serving All Over India – delivering reliable solar solutions nationwide.</p>
        </div>
      </footer>

      {/* Popup Contact Form */}
      {showPopup && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.9 }}
            className="bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4 max-h-[95vh] overflow-y-auto"
          >
            <div className="p-0 sm:p-0">
              {/* Green Top Section with Alternative Energy Image */}
              <div className="relative h-24 bg-cover bg-center bg-no-repeat rounded-t-2xl" style={{
                backgroundImage: `url('https://img.freepik.com/free-photo/alternative-energy-ecological-concept_1157-35707.jpg')`
              }}>
                <div className="absolute inset-0 bg-green-600/60 rounded-t-2xl"></div>
                <div className="relative z-10 p-4 sm:p-6 flex items-center justify-between">
                  <div className="flex items-center">
                    <div className="w-10 h-10 sm:w-12 sm:h-12 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full flex items-center justify-center mr-3">
                      <Sun className="h-5 w-5 sm:h-6 sm:w-6 text-white" />
                    </div>
                    <div>
                      <h2 className="text-lg sm:text-xl font-bold text-white">Contact Moulya solar energies</h2>
                      <p className="text-yellow-200 text-xs sm:text-sm">Get ₹78,000 Subsidy</p>
                    </div>
                  </div>
                  <button
                    onClick={() => setShowPopup(false)}
                    className="text-white hover:text-yellow-200 transition-colors p-1"
                    aria-label="Close popup"
                    title="Close popup"
                  >
                    <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
              
              {/* Form Content */}
              <div className="p-4 sm:p-6">
                <form onSubmit={handlePopupSubmit} className="space-y-3 sm:space-y-4">
                  <div>
                    <label htmlFor="popup-name" className="block text-sm font-medium text-gray-700 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      id="popup-name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-base"
                      placeholder="Enter your full name"
                    />
                  </div>

                  <div>
                    <label htmlFor="popup-phone" className="block text-sm font-medium text-gray-700 mb-1">
                      Phone Number *
                    </label>
                    <input
                      type="tel"
                      id="popup-phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-yellow-500 focus:border-transparent text-base"
                      placeholder="Enter your phone number"
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white font-semibold py-3 px-4 rounded-lg transition-all duration-200 transform hover:scale-105 text-base"
                  >
                    Get Free Consultation
                  </button>
                </form>

                {isSubmitted && (
                  <motion.div
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-4 bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg text-center text-sm"
                  >
                    Thank you! We'll contact you within 30 minutes.
                  </motion.div>
                )}

              </div>
            </div>
          </motion.div>
        </div>
      )}
    </div>
  );
};

export default Landing;
